/** Calendar dates are plain `YYYY-MM-DD` strings in the user's local time zone. */
export type ISODate = string;

export const MEALS = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
export type Meal = (typeof MEALS)[number];

export const MEAL_LABELS: Record<Meal, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snacks',
};

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isISODate(value: string): boolean {
  if (!ISO_RE.test(value)) return false;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}

function toUTC(date: ISODate): number {
  return Date.parse(`${date}T00:00:00Z`);
}

function fromUTC(ms: number): ISODate {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(date: ISODate, days: number): ISODate {
  return fromUTC(toUTC(date) + days * 86_400_000);
}

/** a - b in whole days. */
export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((toUTC(a) - toUTC(b)) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday */
export function dayOfWeek(date: ISODate): number {
  return new Date(toUTC(date)).getUTCDay();
}

/** Monday of the week containing `date`. */
export function weekStart(date: ISODate): ISODate {
  const dow = dayOfWeek(date);
  return addDays(date, dow === 0 ? -6 : 1 - dow);
}

export function rangeDays(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Unknown zones fall back to UTC instead of throwing on every request. */
function safeZone(timeZone: string): string {
  return isValidTimeZone(timeZone) ? timeZone : 'UTC';
}

export function todayIn(timeZone: string, now: Date = new Date()): ISODate {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(timeZone), year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function hourIn(timeZone: string, now: Date = new Date()): number {
  const h = new Intl.DateTimeFormat('en-US', { timeZone: safeZone(timeZone), hour: 'numeric', hourCycle: 'h23' }).format(now);
  return Number(h) % 24;
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Same rule as the existing Redi tracker: 4–11 breakfast, 11–15 lunch, 15–21 dinner, else snack. */
export function mealForHour(hour: number): Meal {
  if (hour >= 4 && hour < 11) return 'breakfast';
  if (hour >= 11 && hour < 15) return 'lunch';
  if (hour >= 15 && hour < 21) return 'dinner';
  return 'snack';
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function weekdayShort(date: ISODate): string {
  return WEEKDAY[dayOfWeek(date)] ?? '';
}

export function monthDay(date: ISODate): string {
  const [, m, d] = date.split('-');
  return `${MONTH[Number(m) - 1] ?? ''} ${Number(d)}`;
}

/** "Mar 3", with the year when it isn't this year's ("Mar 3, 2025"). */
export function shortDay(date: ISODate, today: ISODate): string {
  return date.slice(0, 4) === today.slice(0, 4) ? monthDay(date) : `${monthDay(date)}, ${date.slice(0, 4)}`;
}

export function friendlyDay(date: ISODate, today: ISODate): string {
  const delta = diffDays(date, today);
  if (delta === 0) return 'Today';
  if (delta === -1) return 'Yesterday';
  if (delta === 1) return 'Tomorrow';
  return `${weekdayShort(date)}, ${monthDay(date)}`;
}

export function longDate(date: ISODate): string {
  const [y] = date.split('-');
  return `${weekdayShort(date)}, ${monthDay(date)}, ${y}`;
}
